import { correlationManager, logger } from '@/logging';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiError } from './errors';

// We'll use a function to get the token instead of importing the hook directly
// This avoids circular dependencies and allows for server-side usage
let getAccessTokenFn: (() => Promise<string | null>) | undefined;

/**
 * Set the function to get access tokens
 */
export function setAccessTokenGetter(fn: () => Promise<string | null>): void {
  getAccessTokenFn = fn;
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Create axios instance with default configuration
 */
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor to add authentication token
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Add correlation ID
    const correlationId = correlationManager.getCorrelationId();
    if (correlationId) {
      config.headers['X-Correlation-ID'] = correlationId;
    }

    // Log outgoing request
    logger.debug('API Request', {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      correlationId,
    });

    // Add auth token if available
    if (getAccessTokenFn) {
      try {
        const token = await getAccessTokenFn();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        logger.error('Failed to get access token', { error });
        // Continue without token - let the API handle unauthorized
      }
    }

    return config;
  },
  error => {
    logger.error('API Request Error', { error });
    return Promise.reject(error);
  }
);

/**
 * Response interceptor for error handling and token refresh
 */
apiClient.interceptors.response.use(
  response => {
    // Log successful responses (only in debug mode to avoid noise)
    logger.debug('API Response', {
      status: response.status,
      url: response.config.url,
      method: response.config.method?.toUpperCase(),
      correlationId: response.headers['x-correlation-id'],
    });

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Log the error details
    logger.error('API Error Response', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: originalRequest?.url,
      method: originalRequest?.method?.toUpperCase(),
      error: error.response?.data || error.message,
      correlationId: error.response?.headers?.['x-correlation-id'],
    });

    // Handle 401 errors - try to refresh token once
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      getAccessTokenFn
    ) {
      originalRequest._retry = true;

      logger.info('Attempting token refresh after 401 error');

      try {
        // Get a fresh token - the auth service will handle expiry checking
        const newToken = await getAccessTokenFn();
        if (newToken && newToken !== 'null' && newToken !== '') {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          logger.info('Token refreshed successfully, retrying request');
          return apiClient(originalRequest);
        } else {
          logger.warn('Token refresh returned empty token, triggering re-authentication');
          // Force re-authentication
          if (window.location.pathname !== '/login') {
            window.location.reload();
          }
          return Promise.reject(error);
        }
      } catch (refreshError) {
        logger.error('Token refresh failed during 401 retry', { error: refreshError });
        // If refresh fails, redirect to login or show auth error
        if (window.location.pathname !== '/login') {
          logger.info('Redirecting to login due to token refresh failure');
          // Force a page reload to trigger fresh authentication
          window.location.reload();
        }
        return Promise.reject(error);
      }
    }

    // Transform to ApiError for consistent error handling
    throw ApiError.fromAxiosError(error);
  }
);

/**
 * Helper function to handle API responses
 */
export async function handleApiResponse<T>(
  promise: Promise<{ data: T }>
): Promise<T> {
  const response = await promise;
  return response.data;
}

/**
 * Helper to build query string from params
 */
export function buildQueryString(params?: Record<string, unknown>): string {
  if (!params) return '';

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
}
